"use client"

import { Settings } from "lucide-react"
import { Avatar, AvatarImage, AvatarFallback } from "@/shared/components/ui/avatar"
import { useUIStore } from "@/shared/stores/ui-store"
import type { PublicProfile } from "@/features/profile/actions"
import { useTranslations } from "next-intl"

interface ProfileHeaderProps {
  profile: PublicProfile
  isOwnProfile: boolean
  stats: {
    creations: number
    characters: number
    likes: number
  }
}

function formatStat(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`
  return count.toString()
}

export function ProfileHeader({ profile, isOwnProfile, stats }: ProfileHeaderProps) {
  const t = useTranslations("profile.header")
  const openSettings = useUIStore((s) => s.openSettings)
  const initials = profile.displayName
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase()

  return (
    <div className="pb-2 pt-2">
      {/* Same horizontal layout on all breakpoints: avatar + info */}
      <div className="flex flex-row items-start gap-4 sm:gap-6 lg:gap-8">
        <Avatar className="size-20 shrink-0 ring-2 ring-border sm:size-24 lg:size-32">
          {profile.avatarUrl ? (
            <AvatarImage src={profile.avatarUrl} alt={profile.displayName} />
          ) : null}
          <AvatarFallback className="text-lg font-bold sm:text-2xl lg:text-3xl">
            {initials}
          </AvatarFallback>
        </Avatar>

        <div className="min-w-0 flex flex-1 flex-col items-stretch">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <p className="text-lg font-bold leading-tight text-foreground sm:text-xl">
              @{profile.username}
            </p>
            {isOwnProfile && (
              <button
                type="button"
                onClick={() => openSettings("account", { source: "profile" })}
                className="flex size-9 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label={t("settings")}
              >
                <Settings className="size-5" />
              </button>
            )}
          </div>

          <h1 className="mt-0.5 text-base font-semibold text-foreground lg:mt-1 lg:text-base lg:font-bold">
            {profile.displayName}
          </h1>

          {profile.bio && (
            <p className="mt-1 max-w-md text-sm text-muted-foreground">
              {profile.bio}
            </p>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 sm:mt-4 sm:gap-x-6 lg:gap-x-8">
            <StatItem count={stats.creations} label={t("creations")} />
            <StatItem count={stats.characters} label={t("characters")} />
            <StatItem count={stats.likes} label={t("likes")} />
          </div>
        </div>
      </div>
    </div>
  )
}

function StatItem({ count, label }: { count: number; label: string }) {
  return (
    <div className="flex flex-row items-baseline gap-1.5">
      <span className="text-base font-bold text-foreground sm:text-lg">{formatStat(count)}</span>
      <span className="text-xs text-muted-foreground sm:text-sm">{label}</span>
    </div>
  )
}
